import React from "react";
import moment from "moment";
import { styled } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import { ITeam } from "../../types";
import { IStyledArguments } from "../../types/styled-arguments";
import { ActionLink } from "../atoms";
import { getNextTraining } from "./nextTraining";
import messages from "./PageDashboard.intl";

export const Hero = styled("div")(({ theme }: IStyledArguments) => ({
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 20,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
  },
}));

export const Eyebrow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  fontFamily: theme.typography.alternateFontFamily,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  color: theme.palette.primary.dark,
}));

export const BigTime = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "3.2rem",
  lineHeight: 1.05,
  color: theme.palette.text.primary,
  fontVariantNumeric: "tabular-nums",
  marginTop: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "2.6rem",
  },
}));

export const Subtitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.85rem",
  color: theme.palette.text.secondary,
}));

export const Divider = styled("div")(({ theme }: IStyledArguments) => ({
  borderTop: `1px solid ${theme.palette.extra.borderColor}`,
  margin: theme.spacing(2.25, 0, 2),
}));

const FooterRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const AvatarStack = styled("div")({
  display: "flex",
  alignItems: "center",
});

const MiniAvatar = styled("div")(({ theme }: IStyledArguments) => ({
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: theme.palette.primary.light,
  border: "2px solid #ffffff",
  marginLeft: -8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontSize: "0.62rem",
  fontWeight: 700,
  color: theme.palette.primary.dark,
  "&:first-of-type": {
    marginLeft: 0,
  },
}));

const ConfirmedCount = styled("span")(({ theme }: IStyledArguments) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1),
}));

const EmptyHero = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  color: theme.palette.text.secondary,
  fontSize: "0.9rem",
  padding: theme.spacing(2, 0),
}));

const getInitials = (name: string, surname: string) =>
  `${(name && name[0]) || ""}${(surname && surname[0]) || ""}`.toUpperCase();

interface INextEventHero {
  teams: ITeam[];
}

const NextEventHero = injectIntl<INextEventHero>(({ teams, intl }) => {
  const withTraining = teams
    .map((team) => ({ team, next: getNextTraining(team.trainingSetup) }))
    .filter((entry) => entry.next !== null) as {
    team: ITeam;
    next: { date: Date; time: string };
  }[];

  if (withTraining.length === 0) {
    return (
      <Hero>
        <EmptyHero>{intl.formatMessage(messages.noUpcomingEvent)}</EmptyHero>
      </Hero>
    );
  }

  withTraining.sort((a, b) => (moment(a.next.date).isAfter(b.next.date) ? 1 : -1));
  const { team, next } = withTraining[0];

  const attendance: any[] =
    team.nextTraining && team.nextTraining.trainingAttendance ? team.nextTraining.trainingAttendance : [];
  const confirmed = attendance.filter((entry) => entry.attendance);

  return (
    <Hero>
      <Eyebrow>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3.2 2" />
        </svg>
        {intl.formatMessage(messages.nextTrainings)}
      </Eyebrow>
      <BigTime>{next.time}</BigTime>
      <Subtitle>
        {moment(next.date).isSame(Date.now(), "day")
          ? intl.formatMessage(messages.today)
          : moment(next.date).format("dddd, DD-MM")}
        {" · "}
        {team.name}
      </Subtitle>

      <Divider />

      <FooterRow>
        {confirmed.length > 0 ? (
          <AvatarStack>
            {confirmed.slice(0, 3).map((entry) => (
              <MiniAvatar key={entry.player.id}>
                {getInitials(entry.player.name, entry.player.surname)}
              </MiniAvatar>
            ))}
            <ConfirmedCount>
              {intl.formatMessage(messages.confirmedCount, { n: confirmed.length })}
            </ConfirmedCount>
          </AvatarStack>
        ) : (
          <ConfirmedCount>{intl.formatMessage(messages.noConfirmedYet)}</ConfirmedCount>
        )}
        <ActionLink to={`/teams/${team.id}`} style={{ textTransform: "none", fontWeight: 600 }}>
          {intl.formatMessage(messages.viewDetails)}
        </ActionLink>
      </FooterRow>
    </Hero>
  );
});

export default NextEventHero;
